import React, { MouseEvent, ReactNode } from 'react';
import { Link as ReactLink } from 'react-router-dom';
import styled from 'styled-components';
import { resetButtonStyle } from '@kvdbil/components';
import { translateRoute } from '~/App/shared/localization';
import { isUrlAbsolute } from '~/helpers/url';
import { useTranslation } from '~/Locale';
import {
  domainFromUrl,
  isHostDomain,
  isKvdProdSubdomain,
  isTrustedDomain,
  validURL
} from '~/helpers/router';
import { useSelector } from 'react-redux';
import { localizationSelector } from '~/App/shared/selectors/localization';

const LinkButton = styled.button.attrs({ type: 'button' })`
  ${resetButtonStyle};
  color: ${({ theme }) => theme.colors.info.dark};

  &:focus-visible {
    outline: revert;
  }
`;

const StyledAnchor = styled.a<{ $withUnderline: boolean }>`
  color: ${({ theme }) => theme.colors.info.dark};
  ${({ $withUnderline }) => $withUnderline && 'text-decoration: underline;'}
`;

const StyledReactLink = styled(ReactLink)<{ $withUnderline: boolean }>`
  color: ${({ theme }) => theme.colors.info.dark};
  ${({ $withUnderline }) => $withUnderline && 'text-decoration: underline;'}
`;

export interface LinkProps {
  to?: string | null;
  children: ReactNode;
  isDisabled?: boolean;
  state?: Record<string, unknown>;
  className?: string;
  target?: '_blank';
  rel?: string;
  onClick?(event: MouseEvent): void;
  customAttrs?: { attrName: string; attrValue: string }[];
  noTranslation?: boolean;
  skipAutoNofollowAddition?: boolean;
  withUnderline?: boolean;
}

export default function Link({
  state,
  to = '',
  children,
  isDisabled,
  customAttrs,
  noTranslation = false,
  skipAutoNofollowAddition = false,
  withUnderline = false,
  ...props
}: LinkProps) {
  const { t } = useTranslation();
  const { domainLocale } = useSelector(localizationSelector);

  if (!to) {
    return <LinkButton {...props}>{children}</LinkButton>;
  }

  const customAttrProps: Record<string, string> = {};
  customAttrs?.forEach(({ attrName, attrValue }) => {
    if (attrName in customAttrProps) {
      customAttrProps[attrName] = `${customAttrProps[attrName]} ${attrValue}`;
    } else {
      customAttrProps[attrName] = attrValue;
    }
  });

  if (isUrlAbsolute(to)) {
    const isValidUrl = validURL(to);
    const { rootDomain, subdomain } = domainFromUrl(to);

    if (
      isValidUrl &&
      domainLocale &&
      isHostDomain(rootDomain, domainLocale) &&
      !isKvdProdSubdomain(subdomain)
    ) {
      // In cms app sometimes url are defined as absolute instead of pathname.
      // To support a multilingual website, we must have a pathname+search+hash.
      const parsedUrl = new URL(to);
      to = parsedUrl.pathname + parsedUrl.search + parsedUrl.hash;
    } else {
      if (isValidUrl) {
        if (
          !skipAutoNofollowAddition &&
          !('rel' in customAttrProps) &&
          !isTrustedDomain(rootDomain)
        ) {
          customAttrProps['rel'] = 'nofollow';
        }
        if (!('target' in customAttrProps)) {
          customAttrProps['target'] = '_blank';
        }
      }

      return (
        <StyledAnchor
          href={to}
          {...props}
          {...customAttrProps}
          $withUnderline={withUnderline}
        >
          {children}
        </StyledAnchor>
      );
    }
  }

  const preventDefault = () => (event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
  };

  if (state) {
    return (
      <StyledReactLink
        data-testid="react-link-state"
        to={{ pathname: noTranslation ? to : translateRoute(t, to), state }}
        onClick={isDisabled ? preventDefault() : undefined}
        {...props}
        {...customAttrProps}
        $withUnderline={withUnderline}
      >
        {children}
      </StyledReactLink>
    );
  }

  return (
    <StyledReactLink
      data-testid="react-link"
      to={noTranslation ? to : translateRoute(t, to)}
      onClick={isDisabled ? preventDefault() : undefined}
      {...props}
      {...customAttrProps}
      $withUnderline={withUnderline}
    >
      {children}
    </StyledReactLink>
  );
}
